import {
  ICustomField,
  IShow,
  IStand,
  IStandToCustomField,
} from "@evennt-tech/api-v2-core";
import { getStandSizeValue } from "view/apps/Organiser/pages/Show/Exhibitor/Settings/components/SizeSettingRow";
import { defaultLocalMap } from "tools/show/localMap";
import formatDate from "tools/formats/formatDate";
import { getReadableString } from "constraints/general";
import { IGetPublicInfoShowRes } from "@evennt-tech/api-v2-external";
import { UseTranslateResult } from "@tolgee/react";
import { Locale } from "date-fns";
import { UNRESOLVED_VARIABLE_HTML } from "tools/task/unresolvedVariable";

export type TranslateFn = UseTranslateResult["t"];

// FS-2877 §5/§84: a custom-field variable bound to a specific Show's field id
// resolves only on that Show. When it can't produce a real value (field
// missing, wrong Show, or empty) a soft-warning placeholder is shown instead —
// it never blocks the task.
const UNRESOLVED_TEXT = UNRESOLVED_VARIABLE_HTML;

const getContactOrganiserText = (
  localizedOrganiser?: string | null,
  t?: TranslateFn
) => {
  const organiser = localizedOrganiser?.toLowerCase() ?? "";
  const text = t
    ? t("common.tasks.variables.contact_organiser", { organiser })
    : `Contact ${organiser}`;
  return `&lt;${text}&gt;`;
};

export type ShowWithVariables = Pick<
  IGetPublicInfoShowRes,
  | "venueName"
  | "liveStart"
  | "liveEnd"
  | "name"
  | "id"
  | "venueAddress1"
  | "venueCountry"
  | "venueCity"
  | "postcode"
  | "contactName"
  | "contactEmail"
  | "contactPhoneNumber"
  | "fax"
  | "unitSystem"
  | "dateFormat"
>;

/**
 * Loose `task` shape — only `stand` and `standToCustomFields` are read here.
 */
export type TaskWithStandContext = {
  stand?: IStand;
  standToCustomFields?: IStandToCustomField[];
};

export const generateDefaultVariables = (
  task?: TaskWithStandContext,
  show?: ShowWithVariables,
  localizedOrganiser?: string | null,
  t?: TranslateFn,
  locale?: Locale
): Record<string, string> => {
  const contactOrganiser = getContactOrganiserText(localizedOrganiser, t);

  return {
    "Stand Number": task?.stand?.name || "",
    "Stand Size":
      task?.stand?.size?.length && task?.stand?.size?.width && show
        ? getStandSizeValue(task?.stand?.size, show.unitSystem)
        : contactOrganiser,
    "Open Sides": task?.stand?.openSides
      ? task?.stand.openSides.toString()
      : contactOrganiser,
    "Wall Length": task?.stand?.standWallLength
      ? task?.stand.standWallLength.toString()
      : contactOrganiser,
    "Build Type": task?.stand?.builder?.name || contactOrganiser,
    "Venue name": show?.venueName ?? contactOrganiser,
    "Show start date": show
      ? formatDate(new Date(show.liveStart), getReadableString(show), locale)
      : contactOrganiser,
    "Show end date": show
      ? formatDate(new Date(show.liveEnd), getReadableString(show), locale)
      : contactOrganiser,
    "Show name": show?.name ?? contactOrganiser,
    "Venue Address": show?.venueAddress1 ?? contactOrganiser,
    "Venue City": show?.venueCity ?? contactOrganiser,
    "Venue Country": show?.venueCountry?.name ?? contactOrganiser,
    Postcode: show?.postcode ?? contactOrganiser,
    "Contact Name": show?.contactName ?? contactOrganiser,
    "Contact phone number": show?.contactPhoneNumber ?? contactOrganiser,
    "Contact email": show?.contactEmail ?? contactOrganiser,
    "Additional phone number/Fax": show?.fax ?? contactOrganiser,
  };
};

/**
 * The concrete rendered value of a custom field on this Show's stand, or
 * `null` when the field has no value entered. Shared by the legacy title map
 * (null → contact-organiser fallback) and the id-bound path (null →
 * unresolved).
 */
const getCustomFieldRenderedValue = (
  customField: ICustomField,
  task?: TaskWithStandContext
): string | null => {
  const standCustomField = (task?.standToCustomFields || []).find(
    ({ customFieldId }) => customField.id === customFieldId
  );

  if (!standCustomField) {
    return null;
  }

  const {
    hyperlink,
    customFieldToFiles,
    name,
    text,
    number,
    paragraph,
  } = standCustomField;

  if (hyperlink) {
    const safeHyperlink = hyperlink.replace(/&/g, "&amp;");
    return `<a href="${safeHyperlink}" style="text-decoration: none;" target="_blank">${
      name || safeHyperlink
    }</a>`;
  }
  if (customFieldToFiles && customFieldToFiles.length > 0) {
    return customFieldToFiles
      .map(
        (customFieldToFile) =>
          `<a href="${
            customFieldToFile!.file!.url
          }" target="_blank" style="text-decoration: none;">${
            customFieldToFile!.file!.title
          }</a>`
      )
      .join(" ");
  }
  if (text || number?.toString() || paragraph) {
    return text || number?.toString() || paragraph || null;
  }
  return null;
};

type GetCustomFieldsMapParams = {
  task?: TaskWithStandContext;
  show?: ShowWithVariables;
  customFields?: ICustomField[];
  localizedOrganiser?: string | null;
  t?: TranslateFn;
  locale?: Locale;
};
export const getCustomFieldsMap = ({
  task,
  show,
  customFields = [],
  localizedOrganiser,
  t,
  locale,
}: GetCustomFieldsMapParams) => {
  const contactOrganiser = getContactOrganiserText(localizedOrganiser, t);
  const defaultVariables = generateDefaultVariables(
    task,
    show,
    localizedOrganiser,
    t,
    locale
  );

  if (customFields.length) {
    const customFieldVariables: Record<string, string> = {};

    customFields.forEach((customField) => {
      // The token in a description is keyed by the ENGLISH title, always — so
      // a localised read has to match on `sourceTitle`, not the substituted
      // `title`, or the placeholder stops matching and gets stripped.
      const title = customField.sourceTitle ?? customField.title;
      customFieldVariables[title] =
        getCustomFieldRenderedValue(customField, task) ?? contactOrganiser;
    });

    return {
      ...defaultVariables,
      ...customFieldVariables,
    };
  }

  return defaultVariables;
};

export const decodeHtmlEntities = (str: string): string => {
  const htmlEntities: { [key: string]: string } = {
    "&amp;": "&",
    "&lt;": "<",
    "&gt;": ">",
    // eslint-disable-next-line quotes
    "&quot;": '"',
    "&#39;": "'",
    "&nbsp;": " ",
  };

  return Object.entries(htmlEntities).reduce((result, [key, value]) => {
    return result.replace(new RegExp(key, "g"), value);
  }, str);
};

export const replaceLineBreaksWithNewlines = (html: string): string => {
  if (!html) {
    return "";
  }
  // Capture <a> tags with hrefs and replace with a placeholder
  const anchors: Record<string, string>[] = [];
  html = html.replace(
    /<a[^>]+href="([^"]+)"[^>]*>([^<]+)<\/a>/g,
    (match, href, content) => {
      anchors.push({ href, content });
      return `LINK_PLACEHOLDER_${anchors.length - 1}`;
    }
  );

  // Remove all other HTML tags and entities
  html = html
    .replace(/<\/p>/g, "\n")
    .replace(/<p>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");

  // Restore the <a> tags with hrefs
  anchors.forEach((anchor, index) => {
    html = html.replace(
      `LINK_PLACEHOLDER_${index}`,
      `<a href="${anchor.href}">${anchor.content}</a>`
    );
  });

  return html;
};

type GetTextWithVariables = {
  task?: TaskWithStandContext;
  show?: ShowWithVariables | null;
  customFields?: ICustomField[];
  description: string;
  contrastColor?: string;
  localizedOrganiser?: string | null;
  t?: TranslateFn;
  locale?: Locale;
};

// FS-2877 §5/§143: an id-bound custom-field token. The editor keeps the visible
// `‹Title›` text and stamps the field id onto the wrapping span
// (`data-cf-id`). Quill emits both the colour style and the attribute on one
// span, in either order, so the id is matched anywhere in the tag.
const CUSTOM_FIELD_ID_TOKEN = /<span\b[^>]*\bdata-cf-id="([^"]*)"[^>]*>\s*&lt;([\s\S]*?)&gt;\s*<\/span>/g;

const VARIABLE_COLOR = "rgb(41, 121, 255)";
const VARIABLE_TOKEN = /<[^<>]+>/;

/**
 * The editor paints variable tokens #2979FF; this swaps that for `color` on
 * render. Users can pick #2979FF for any text or background too, so only the
 * `color` of an element holding a `<Variable>` token is replaced. Text typed
 * right after a variable shares its span, so the token need not be alone.
 */
export const recolorVariables = (html: string, color: string): string => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  let changed = false;
  doc.body.querySelectorAll<HTMLElement>("[style]").forEach((element) => {
    if (
      element.style.color === VARIABLE_COLOR &&
      VARIABLE_TOKEN.test(element.textContent ?? "")
    ) {
      element.style.color = color;
      changed = true;
    }
  });
  return changed ? doc.body.innerHTML : html;
};

const getTextWithVariables = ({
  task,
  show,
  customFields,
  description,
  contrastColor,
  localizedOrganiser = defaultLocalMap.organiser,
  t,
  locale,
}: GetTextWithVariables): string => {
  if (!show) {
    return "";
  }
  const variableMapping = getCustomFieldsMap({
    task,
    show,
    customFields,
    localizedOrganiser,
    t,
    locale,
  });

  // Id-bound custom-field tokens FIRST — a field bound to a specific Show only
  // resolves when it belongs to the Show being rendered (`showId ===
  // currentShowId`); a same-name field on another Show is a different id and
  // stays unresolved. Runs before the generic pass so the inner `‹Title›` is
  // consumed here rather than by the legacy title match.
  let result = description
    ? recolorVariables(description, contrastColor || "rgb(0, 0, 0)")
    : description;

  result = result?.replace(CUSTOM_FIELD_ID_TOKEN, (match, id, _title) => {
    const customField = (customFields || []).find((field) => field.id === id);
    if (!customField || customField.showId !== show.id) {
      return UNRESOLVED_TEXT;
    }
    return getCustomFieldRenderedValue(customField, task) ?? UNRESOLVED_TEXT;
  });

  // Legacy title-only tokens (no id) — existing tasks keep resolving by title.
  const regex = /&lt;([\s\S]*?)&gt;/g;
  result = result?.replace(regex, (match, content) => {
    const decodedContent = decodeHtmlEntities(content);
    return variableMapping[decodedContent] || match;
  });
  return result;
};

export default getTextWithVariables;
